import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * VillaAmbient
 * ------------
 * A very quiet, self-generated ambient bed for the villa:
 *   • distant Aegean surf (filtered pink-ish noise, slowly modulated)
 *   • night crickets (short soft chirps scheduled on a Poisson-ish clock)
 *
 * The whole thing is synthesized with the Web Audio API so we don't need to
 * ship or license any audio file. Master gain is clamped very low so it
 * remains a background whisper.
 *
 * Autoplay policies require a user gesture — we start (or resume) the
 * AudioContext the first time the villa is unlocked (rs_villa_entered === "1"),
 * which is always the direct result of the "Enter the Villa" click.
 */
const START_GAIN = 0.09; // ceiling — quiet enough to keep a room, loud enough to feel
const FADE_MS = 2400;

const VillaAmbient = () => {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const cricketTimerRef = useRef(null);
  const waveTimerRef = useRef(null);
  const gullTimerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("rs_ambient_muted") === "1",
  );

  // Start / stop management — kicked off whenever the villa is entered
  useEffect(() => {
    const kick = () => {
      if (localStorage.getItem("rs_villa_entered") === "1" && !running && !muted) {
        start();
      }
    };
    kick();
    window.addEventListener("storage", kick);
    // Also react to same-tab updates via a custom event
    window.addEventListener("rs-villa-entered", kick);
    return () => {
      window.removeEventListener("storage", kick);
      window.removeEventListener("rs-villa-entered", kick);
      stop();
    };
  }, []);

  const start = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      ctxRef.current = ctx;

      // === Master output — starts silent, fades in over FADE_MS ===
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, ctx.currentTime);
      master.gain.exponentialRampToValueAtTime(START_GAIN, ctx.currentTime + FADE_MS / 1000);
      master.connect(ctx.destination);
      masterRef.current = master;

      // === Distant surf underbed — quieter, steady low rumble ===
      // (Kept softer than before because individual wave crashes now sit on top.)
      const bufSec = 4;
      const buf = ctx.createBuffer(1, ctx.sampleRate * bufSec, ctx.sampleRate);
      const ch = buf.getChannelData(0);
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < ch.length; i++) {
        // Cheap Voss/McCartney-ish pink noise
        const white = Math.random() * 2 - 1;
        b0 = 0.99 * b0 + white * 0.02;
        b1 = 0.95 * b1 + white * 0.05;
        b2 = 0.85 * b2 + white * 0.15;
        ch[i] = b0 + b1 + b2 + white * 0.1;
      }
      const surf = ctx.createBufferSource();
      surf.buffer = buf;
      surf.loop = true;
      const surfLP = ctx.createBiquadFilter();
      surfLP.type = "lowpass";
      surfLP.frequency.value = 320;   // deeper — leaves headroom for the crashes above
      surfLP.Q.value = 0.6;
      const surfHP = ctx.createBiquadFilter();
      surfHP.type = "highpass";
      surfHP.frequency.value = 65;    // trim sub-bass so it doesn't rumble on speakers
      // Very slow LFO — the deep water breathes every ~14s
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.07;
      const lfoDepth = ctx.createGain();
      lfoDepth.gain.value = 70;
      lfo.connect(lfoDepth).connect(surfLP.frequency);
      lfo.start();
      const surfGain = ctx.createGain();
      surfGain.gain.value = 0.45;     // dropped from 0.85 — now a bed, not the star
      surf.connect(surfHP).connect(surfLP).connect(surfGain).connect(master);
      surf.start();

      // === Gentle wave crashes — a swell rising, breaking, and hissing back ===
      // Each crash is a burst of filtered noise with a slow amp swell, a brief
      // brighter "break" at the peak, and a long tail. Scheduled every 6–11s
      // so the shoreline feels alive but never busy.
      const scheduleWave = () => {
        const c = ctxRef.current;
        if (!c) return;
        // ---- Deep body of the wave (low-mid rumble that swells and recedes)
        const bodyLen = 6; // seconds of noise buffer per wave
        const bodyBuf = c.createBuffer(1, c.sampleRate * bodyLen, c.sampleRate);
        const bd = bodyBuf.getChannelData(0);
        let p0 = 0, p1 = 0, p2 = 0;
        for (let i = 0; i < bd.length; i++) {
          const w = Math.random() * 2 - 1;
          p0 = 0.99 * p0 + w * 0.02;
          p1 = 0.95 * p1 + w * 0.05;
          p2 = 0.85 * p2 + w * 0.15;
          bd[i] = p0 + p1 + p2 + w * 0.08;
        }
        const body = c.createBufferSource();
        body.buffer = bodyBuf;
        const bodyLP = c.createBiquadFilter();
        bodyLP.type = "lowpass";
        bodyLP.Q.value = 0.7;
        const bodyHP = c.createBiquadFilter();
        bodyHP.type = "highpass";
        bodyHP.frequency.value = 80;
        const bodyGain = c.createGain();
        // Randomise a little so no two crashes are identical
        const t0 = c.currentTime + 0.05;
        const swell = 1.6 + Math.random() * 0.8;   // 1.6–2.4s rise
        const tail  = 3.2 + Math.random() * 1.4;   // 3.2–4.6s decay
        const peakG = 0.55 + Math.random() * 0.25;
        // Amp envelope: silent → swell → peak → long tail
        bodyGain.gain.setValueAtTime(0.0001, t0);
        bodyGain.gain.exponentialRampToValueAtTime(peakG, t0 + swell);
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, t0 + swell + tail);
        // Filter sweep: cutoff rises with the swell (wave forming), then falls
        // back down as it recedes into the deep.
        bodyLP.frequency.setValueAtTime(280, t0);
        bodyLP.frequency.exponentialRampToValueAtTime(900, t0 + swell);
        bodyLP.frequency.exponentialRampToValueAtTime(240, t0 + swell + tail);
        body.connect(bodyHP).connect(bodyLP).connect(bodyGain).connect(masterRef.current);
        body.start(t0);
        body.stop(t0 + swell + tail + 0.3);

        // ---- The "break" — a shorter, brighter hiss right at the crest
        const brkLen = 2.2;
        const brkBuf = c.createBuffer(1, c.sampleRate * brkLen, c.sampleRate);
        const bkd = brkBuf.getChannelData(0);
        for (let i = 0; i < bkd.length; i++) bkd[i] = Math.random() * 2 - 1;
        const brk = c.createBufferSource();
        brk.buffer = brkBuf;
        const brkBP = c.createBiquadFilter();
        brkBP.type = "bandpass";
        brkBP.frequency.value = 2400;
        brkBP.Q.value = 0.8;
        const brkGain = c.createGain();
        const bt0 = t0 + swell - 0.15;
        const brkPk = 0.18 + Math.random() * 0.08;
        brkGain.gain.setValueAtTime(0.0001, bt0);
        brkGain.gain.exponentialRampToValueAtTime(brkPk, bt0 + 0.25);
        brkGain.gain.exponentialRampToValueAtTime(0.0001, bt0 + 1.8);
        brk.connect(brkBP).connect(brkGain).connect(masterRef.current);
        brk.start(bt0);
        brk.stop(bt0 + brkLen);

        // Next wave in 6–11s
        const next = 6000 + Math.random() * 5000;
        waveTimerRef.current = window.setTimeout(scheduleWave, next);
      };
      // First wave lands after a moment so the room settles first
      waveTimerRef.current = window.setTimeout(scheduleWave, 1400);

      // === Distant Aegean night birds — a soft two-note call every 22–52s ===
      // A stylised Scops-owl-ish call: two short, slightly detuned tones with
      // a mellow bandpass shaping and a whisper of decay. Kept well below the
      // waves so it feels like a bird a headland away.
      const scheduleGull = () => {
        const c = ctxRef.current;
        if (!c) return;
        const t0 = c.currentTime + 0.05;
        const notes = 2;
        const gap = 0.35 + Math.random() * 0.25; // spacing between the two calls
        const baseHz = 780 + Math.random() * 260; // 780–1040 Hz
        // Alternate between rising and falling second note for variety
        const second = Math.random() < 0.5 ? baseHz * 0.86 : baseHz * 1.12;
        for (let n = 0; n < notes; n++) {
          const hz = n === 0 ? baseHz : second;
          const nt = t0 + n * gap;
          const osc = c.createOscillator();
          osc.type = "sine";
          osc.frequency.value = hz;
          // Gentle detune wobble makes it feel organic, not chirpy-electronic
          const detune = c.createOscillator();
          detune.frequency.value = 6 + Math.random() * 4;
          const detuneGain = c.createGain();
          detuneGain.gain.value = 4 + Math.random() * 3;
          detune.connect(detuneGain).connect(osc.frequency);
          detune.start(nt);
          detune.stop(nt + 0.7);
          const bp = c.createBiquadFilter();
          bp.type = "bandpass";
          bp.frequency.value = hz;
          bp.Q.value = 3;
          const g = c.createGain();
          const peak = 0.08 + Math.random() * 0.04;
          g.gain.setValueAtTime(0.0001, nt);
          g.gain.exponentialRampToValueAtTime(peak, nt + 0.06);
          g.gain.setValueAtTime(peak, nt + 0.18);
          g.gain.exponentialRampToValueAtTime(0.0001, nt + 0.55);
          osc.connect(bp).connect(g).connect(masterRef.current);
          osc.start(nt);
          osc.stop(nt + 0.6);
        }
        // Next call in 22–52s (they're supposed to feel rare and lonely)
        const next = 22000 + Math.random() * 30000;
        gullTimerRef.current = window.setTimeout(scheduleGull, next);
      };
      // First bird arrives after ~9–16s so it lands into an already-established scene
      gullTimerRef.current = window.setTimeout(scheduleGull, 9000 + Math.random() * 7000);


      // === Crickets — occasional short chirps, high-Q filtered noise blips ===
      const scheduleCricket = () => {
        const c = ctxRef.current;
        if (!c) return;
        const noise = c.createBufferSource();
        const n = c.createBuffer(1, c.sampleRate * 0.08, c.sampleRate);
        const nd = n.getChannelData(0);
        for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
        noise.buffer = n;
        const bp = c.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 4200 + Math.random() * 1400; // 4.2 – 5.6 kHz
        bp.Q.value = 22;
        const g = c.createGain();
        const t0 = c.currentTime + 0.02;
        // Rapid tick envelope — cricket-y triplet
        for (let k = 0; k < 3; k++) {
          const on = t0 + k * 0.045;
          g.gain.setValueAtTime(0.0001, on);
          g.gain.exponentialRampToValueAtTime(0.35, on + 0.008);
          g.gain.exponentialRampToValueAtTime(0.0001, on + 0.03);
        }
        noise.connect(bp).connect(g).connect(masterRef.current);
        noise.start(t0);
        noise.stop(t0 + 0.2);

        // Next chirp scheduled in 1.6 – 5.5s (Poisson-ish variation)
        const next = 1600 + Math.random() * 3900;
        cricketTimerRef.current = window.setTimeout(scheduleCricket, next);
      };
      // Kick off the first cricket after a short pause so surf is heard first
      cricketTimerRef.current = window.setTimeout(scheduleCricket, 2200);

      setRunning(true);
    } catch (e) {
      // Silently ignore — ambient audio is a nicety, never critical
      console.warn("VillaAmbient: unable to start", e);
    }
  };

  const stop = () => {
    if (cricketTimerRef.current) window.clearTimeout(cricketTimerRef.current);
    cricketTimerRef.current = null;
    if (waveTimerRef.current) window.clearTimeout(waveTimerRef.current);
    waveTimerRef.current = null;
    if (gullTimerRef.current) window.clearTimeout(gullTimerRef.current);
    gullTimerRef.current = null;
    const master = masterRef.current;
    const ctx = ctxRef.current;
    if (master && ctx) {
      try {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
        master.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
      } catch { /* fine to ignore — context may already be closing */ }
      window.setTimeout(() => {
        try { ctx.close(); } catch { /* already closed */ }
      }, 700);
    }
    ctxRef.current = null;
    masterRef.current = null;
    setRunning(false);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem("rs_ambient_muted", next ? "1" : "0");
    if (next) {
      stop();
    } else if (localStorage.getItem("rs_villa_entered") === "1") {
      start();
    }
  };

  // Nothing on splash — the villa hasn't been entered yet
  const enteredVilla =
    typeof window !== "undefined" && localStorage.getItem("rs_villa_entered") === "1";
  if (!enteredVilla) return null;

  return (
    <button
      type="button"
      data-testid="villa-ambient-toggle"
      onClick={toggleMute}
      className="fixed bottom-6 left-6 lg:left-auto lg:right-6 z-40 w-9 h-9 rounded-full border flex items-center justify-center transition backdrop-blur-sm hover:opacity-100 opacity-70"
      style={{
        borderColor: "var(--rs-border)",
        background: "rgba(10,7,10,0.75)",
        color: muted ? "var(--rs-muted-bone, #9a8f83)" : "var(--rs-terracotta, #c07050)",
      }}
      title={muted ? "Villa is quiet — click to wake it" : "Villa ambient — click to hush"}
      aria-label="Toggle villa ambient audio"
    >
      {muted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
    </button>
  );
};

export default VillaAmbient;
